import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { InvoiceListItemDTO } from "../types/dtos";
import type { InvoiceDraftSelection } from "@/app/_services/InvoiceService/invoiceSelection";

export const invoicesApi = createApi({
  reducerPath: "invoicesApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api/invoices/" }),
  tagTypes: ["Invoices", "InvoiceOptions"],
  endpoints: (b) => ({
    getInvoiceItemOptions: b.query<any[], void>({
      query: () => ({ url: `itemoptions/` }),
      transformResponse: (res: any) => res ?? [],
      providesTags: [{ type: "InvoiceOptions", id: "ITEMS" }],
    }),
    getInvoiceById: b.query<any, number | string>({
      query: (id) => ({ url: `${id}` }),
      providesTags: (_res, _err, id) => [{ type: "Invoices", id }],
    }),
    getDraftInvoices: b.query<InvoiceListItemDTO[], { siteId?: number } | void>({
      query: () => ({ url: `draftinvoices/` }),
      transformResponse: (res: any): InvoiceListItemDTO[] =>
        Array.isArray(res?.drafts) ? (res.drafts as InvoiceListItemDTO[]) : [],
      providesTags: [{ type: "Invoices", id: "DRAFTS" }],
      keepUnusedDataFor: 300, // 5 min – verbessert Navigation zwischen Tabs
    }),
    getFinalInvoices: b.query<InvoiceListItemDTO[], { siteId?: number } | void>({
      query: () => ({ url: `finalinvoices/` }),
      transformResponse: (res: any): InvoiceListItemDTO[] =>
        Array.isArray(res?.finals) ? (res.finals as InvoiceListItemDTO[]) : [],
      providesTags: [{ type: "Invoices", id: "FINALS" }],
      keepUnusedDataFor: 300,
    }),
    getInvoiceChannelOptions: b.query<any[], void>({
      query: () => ({ url: `channeloptions/` }),
      providesTags: [{ type: "InvoiceOptions", id: "CHANNELS" }],
      keepUnusedDataFor: 3600, // 1h – seltene Änderungen
    }),
    getInvoiceTypeOptions: b.query<any[], void>({
      query: () => ({ url: `typeoptions/` }),
      providesTags: [{ type: "InvoiceOptions", id: "TYPES" }],
      keepUnusedDataFor: 3600,
    }),
    // Update a full invoice payload
    updateInvoice: b.mutation<any, any>({
      query: (data) => ({ url: `invoice`, method: "PATCH", body: data }),
      invalidatesTags: (_res, _err, data) => [
        { type: "Invoices", id: data?.AutoID ?? data?.ID },
        { type: "Invoices", id: "DRAFTS" },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        // Optimistisch den Detail-Cache aktualisieren
        const id = arg?.AutoID ?? arg?.ID ?? arg?.id;
        const patch = id
          ? dispatch(
              invoicesApi.util.updateQueryData("getInvoiceById", id, (draft: any) => {
                Object.assign(draft, arg);
              })
            )
          : null;
        try {
          await queryFulfilled;
        } catch {
          // Rollback bei Fehler
          patch?.undo?.();
        }
      },
    }),
    // Create an invoice line
    createInvoiceLine: b.mutation<any, any>({
      query: (data) => ({ url: `invoiceline`, method: "POST", body: data }),
      invalidatesTags: (_res, _err, data) => [
        { type: "Invoices", id: data?.Invoice_ID },
      ],
    }),
    // Delete draft/final invoice by id
    deleteInvoice: b.mutation<void, number | string>({
      query: (id) => ({ url: `${id}/`, method: "DELETE" }),
      invalidatesTags: [
        { type: "Invoices", id: "DRAFTS" },
        { type: "Invoices", id: "FINALS" },
      ],
    }),
    // Create drafts for booking ids
    createDrafts: b.mutation<
      any,
      {
        ids: number[];
        locale: string;
        force?: boolean;
        selections?: InvoiceDraftSelection[];
      }
    >({
      query: ({ ids, locale, force, selections }) => ({
        url: `createdata`,
        method: "POST",
        body: {
          ids,
          locale,
          ...(force ? { force } : {}),
          ...(Array.isArray(selections) && selections.length > 0
            ? { selections }
            : {}),
        },
      }),
      invalidatesTags: (res, _err, { ids }) => [
        { type: "Invoices", id: "DRAFTS" },
        // Also refresh per-booking invoice lists
        ...ids.map((bid) => ({ type: "Invoices" as const, id: `BY_BOOKING:${bid}` })),
      ],
    }),
    // Invoice for booking helper
    getInvoiceForBooking: b.query<any, void | string>({
      // Optional bookingId param; if omitted, server may infer, but recommended to pass
      query: (bookingId) => ({ url: `invoiceForBooking/${bookingId ?? ""}` }),
    }),
    // Create empty invoice for a customer
    createEmptyData: b.mutation<any, { customerId: number; locale: string }>({
      query: ({ customerId, locale }) => ({
        url: `createemptydata`,
        method: "POST",
        body: { customerId, locale },
      }),
      invalidatesTags: [{ type: "Invoices", id: "DRAFTS" }],
    }),
    // List invoices for a booking (drafts and finals)
    getInvoicesByBooking: b.query<
      { AutoID: number; InvoiceNumber: string | null; FilePath: string | null; InvoiceTypes_ID: number | null; InvoiceTypes?: { Code?: string | null; Name1?: string | null } | null; CreatedAt: string | null; EditedAt: string | null }[],
      string | number
    >({
      query: (bookingId) => ({ url: `bybooking/${bookingId}` }),
      transformResponse: (res: any) => res?.invoices ?? [],
      providesTags: (result, _err, bookingId) => [
        { type: "Invoices", id: `BY_BOOKING:${bookingId}` },
      ],
    }),
    // Generate a PDF/ZIP; store as base64 + contentType to keep cache serializable
    generatePdf: b.query<{ base64: string; contentType: string }, { ids: number[] | number; zip?: boolean }>({
      query: ({ ids, zip }) => {
        const params = Array.isArray(ids)
          ? ids.map((id) => `ids=${id}`).join("&")
          : `ids=${ids}`;
        const query = zip ? `${params}&zip=true` : params;
        return {
          url: `generatepdf?${query}`,
          // Convert blob to base64 string with contentType
          responseHandler: async (response: Response) => {
            const contentType =
              response.headers.get("content-type") || "application/pdf";
            const contentDisposition =
              response.headers.get("content-disposition") || "";
            const filenameMatch =
              /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(
                contentDisposition
              );
            const filename = filenameMatch
              ? decodeURIComponent(filenameMatch[1] || filenameMatch[2] || "")
              : undefined;
            const blob = await response.blob();
            const base64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const result = reader.result as string;
                // result is like 'data:application/pdf;base64,....'
                const b64 = result.split(",")[1] || "";
                resolve(b64);
              };
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            return { base64, contentType, filename };
          },
        } as any;
      },
      providesTags: [],
    }),
    // Send invoice via mail (expects base64 attachment provided by caller)
    sendInvoiceMail: b.mutation<
      any,
      { to: string; subject: string; html: string; attachments: any[] }
    >({
      query: (body) => ({ url: `sendmail`, method: "POST", body }),
    }),
  }),
});

export const {
  useGetDraftInvoicesQuery,
  useGetFinalInvoicesQuery,
  useGetInvoiceChannelOptionsQuery,
  useLazyGetInvoiceChannelOptionsQuery,
  useGetInvoiceTypeOptionsQuery,
  useLazyGetInvoiceTypeOptionsQuery,
  useGetInvoiceItemOptionsQuery,
  useLazyGetInvoiceItemOptionsQuery,
  useGetInvoiceByIdQuery,
  useLazyGetInvoiceByIdQuery,
  useUpdateInvoiceMutation,
  useCreateInvoiceLineMutation,
  useDeleteInvoiceMutation,
  useCreateDraftsMutation,
  useCreateEmptyDataMutation,
  useLazyGeneratePdfQuery,
  useSendInvoiceMailMutation,
  useGetInvoiceForBookingQuery,
  useGetInvoicesByBookingQuery,
} = invoicesApi;
